import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LuArrowLeft, LuBug, LuRefreshCw, LuTrophy } from 'react-icons/lu';
import { ContestStateBadge } from './ContestStateBadge';
import { Button } from '../../ui/Button';
import { Alert, EmptyState, Skeleton } from '../../ui/Feedback';
import { Pagination } from '../../ui/Navigation';
import { Avatar } from '../../ui/Labels';
import { useDocumentTitle, useStrings } from '../../tenant/TenantProvider';
import { useAuthStore } from '../../stores/auth';
import { contestApi } from '../../lib/contestApi';
import { formatElapsed, formatTime } from '../../lib/format';
import { useNow } from '../../hooks/useNow';
import { cx } from '../../ui/cx';

const REFRESH_COOLDOWN_MS = 60_000;

function WrongAttempts({ count }) {
  const t = useStrings();
  return (
    <span className="standings-cell__wrong" title={t('standings.wrongAttempts', { count })}>
      <LuBug aria-hidden="true" />
      <span className="tabular">{count}</span>
      <span className="visually-hidden">{t('standings.wrongAttempts', { count })}</span>
    </span>
  );
}

function ProblemCell({ cell }) {
  const wrong = cell?.wrongAttempts ?? 0;
  if (!cell || (!cell.solved && wrong === 0)) return <span className="subtle">–</span>;
  if (!cell.solved) return <WrongAttempts count={wrong} />;
  return (
    <span className="standings-cell">
      <span className="standings-cell__time mono">{formatElapsed(cell.acceptedSeconds ?? 0)}</span>
      {wrong > 0 && <WrongAttempts count={wrong} />}
    </span>
  );
}

function YourStanding({ me, problemCount, penaltyMinutes }) {
  const t = useStrings();
  const facts = [
    { label: t('standings.columnRank'), value: `#${me.rank}` },
    { label: t('standings.columnScore'), value: me.score },
    {
      label: t('standings.columnFinish'),
      value: me.score > 0 ? formatElapsed(me.finishTimeSeconds) : '—',
      note: me.penaltyCount > 0 ? t('standings.penaltyNote', { minutes: me.penaltyCount * penaltyMinutes }) : null,
    },
    { label: t('contest.solved'), value: `${me.solvedCount}/${problemCount}` },
  ];
  return (
    <section className="your-standing lit" aria-label={t('standings.yourStanding')}>
      <h2 className="your-standing__title">{t('standings.yourStanding')}</h2>
      <dl className="your-standing__facts">
        {facts.map((fact) => (
          <div key={fact.label}>
            <dt>{fact.label}</dt>
            <dd className="mono">{fact.value}</dd>
            {fact.note && <p className="your-standing__note">{fact.note}</p>}
          </div>
        ))}
      </dl>
    </section>
  );
}

export function StandingsPage() {
  const { contestId } = useParams();
  const t = useStrings();
  const { user } = useAuthStore();
  const now = useNow(1000);
  const [contest, setContest] = useState(null);
  const [board, setBoard] = useState(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState(() => Date.now());

  useDocumentTitle(contest ? `${t('standings.title')} · ${contest.name}` : t('standings.title'));

  const fetchBoard = useCallback(
    async (target) => {
      const response = await contestApi.leaderboard(contestId, target);
      if (response.success) {
        setBoard(response.data);
        setPage(target);
        setError(null);
        return true;
      }
      setError(response.code === 'LEADERBOARD_UNAVAILABLE' ? t('standings.unavailable') : response.error);
      return false;
    },
    [contestId, t]
  );

  const loadAll = useCallback(async () => {
    setStatus('loading');
    const detail = await contestApi.detail(contestId);
    if (!detail.success) {
      setError(detail.status === 404 ? t('contest.notFound') : detail.error);
      setStatus('error');
      return;
    }
    setContest(detail.data.contest);
    const ok = await fetchBoard(1);
    setLastRefreshAt(Date.now());
    setStatus(ok ? 'ready' : 'error');
  }, [contestId, fetchBoard, t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const cooldownMs = Math.max(0, lastRefreshAt + REFRESH_COOLDOWN_MS - now);

  const refresh = async () => {
    if (cooldownMs > 0 || refreshing) return;
    setRefreshing(true);
    await fetchBoard(page);
    setLastRefreshAt(Date.now());
    setRefreshing(false);
  };

  const goTo = async (target) => {
    setRefreshing(true);
    await fetchBoard(target);
    setRefreshing(false);
  };

  return (
    <div className="page standings-page">
      <Link to={`/contests/${contestId}`} className="back-link" viewTransition>
        <LuArrowLeft aria-hidden="true" />
        {contest?.name ?? t('contest.back')}
      </Link>
      <header className="page-header">
        <div>
          <div className="contest-page__title-row">
            <h1 className="page-title">{t('standings.title')}</h1>
            {contest && <ContestStateBadge state={board?.final ? 'finalized' : contest.state} />}
          </div>
          {board && (
            <p className="page-subtitle">
              {board.final ? t('standings.final') : t('standings.live')}
              {board.generatedAt && ` · ${t('standings.updatedAt', { time: formatTime(board.generatedAt) })}`}
            </p>
          )}
        </div>
        {board && !board.final && (
          <Button icon={LuRefreshCw} loading={refreshing} disabled={cooldownMs > 0} onClick={refresh}>
            {cooldownMs > 0 ? t('standings.refreshIn', { seconds: Math.ceil(cooldownMs / 1000) }) : t('standings.refresh')}
          </Button>
        )}
      </header>

      {status === 'loading' && (
        <div className="history-skeleton">
          <Skeleton height={72} />
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </div>
      )}

      {status === 'error' && (
        <Alert tone={error === t('standings.unavailable') ? 'info' : 'danger'} action={<Button size="sm" onClick={loadAll}>{t('common.retry')}</Button>}>
          {error}
        </Alert>
      )}

      {status === 'ready' && board && contest && (
        <>
          {board.me ? (
            <YourStanding me={board.me} problemCount={board.problems.length} penaltyMinutes={contest.penaltyMinutes} />
          ) : (
            <p className="muted your-standing__empty">{t('standings.notRanked')}</p>
          )}

          {board.rows.length === 0 ? (
            <div className="panel">
              <EmptyState icon={LuTrophy} title={t('standings.emptyTitle')} text={t('standings.emptyText')} />
            </div>
          ) : (
            <div className="table-card standings-scroll">
              <table className="data-table standings-table">
                <thead>
                  <tr>
                    <th className="standings-table__rank">{t('standings.columnRank')}</th>
                    <th>{t('standings.columnParticipant')}</th>
                    <th className="is-numeric">{t('standings.columnScore')}</th>
                    <th className="is-numeric">{t('standings.columnFinish')}</th>
                    {board.problems.map((problem) => (
                      <th key={problem.id} className="is-center">
                        <span className="standings-table__problem">{problem.label}</span>
                        <span className="standings-table__points tabular">{problem.points}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="stagger">
                  {board.rows.map((row, index) => {
                    const mine = row.userId === user?.id;
                    return (
                      <tr key={row.userId} className={cx(mine && 'is-me')} style={{ '--i': Math.min(index, 12) }}>
                        <td className="standings-table__rank">
                          <span className={cx('rank', row.rank <= 3 && `rank--${row.rank}`)}>{row.rank}</span>
                        </td>
                        <td>
                          <span className="standings-table__person">
                            <Avatar name={row.username} />
                            <span className="standings-table__name">{row.username}</span>
                            {mine && <span className="badge badge--accent standings-table__you">{t('standings.you')}</span>}
                          </span>
                        </td>
                        <td className="is-numeric mono">{row.score}</td>
                        <td className="is-numeric">
                          <span className="mono">{row.score > 0 ? formatElapsed(row.finishTimeSeconds) : '—'}</span>
                          {row.penaltyCount > 0 && (
                            <span className="standings-table__penalty">{t('standings.penaltyNote', { minutes: row.penaltyCount * contest.penaltyMinutes })}</span>
                          )}
                        </td>
                        {board.problems.map((problem) => (
                          <td key={problem.id} className="is-center">
                            <ProblemCell cell={row.problems[String(problem.id)]} />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            page={page}
            pages={board.pageCount}
            onChange={goTo}
            disabled={refreshing}
            summary={t('standings.page', { page, pages: Math.max(board.pageCount, 1) })}
            previousLabel={t('common.previous')}
            nextLabel={t('common.next')}
          />
        </>
      )}
    </div>
  );
}
